import React from 'react';
import styled from 'styled-components';

import {
  ButtonPrimary,
  ButtonTertiary,
  TextLink,
  Type,
} from '@spotify-internal/encore-creator-web';
import {
  gray50,
  screenSmMax,
  screenSmMin,
  screenXsMax,
  kleinBlue,
} from '@spotify-internal/encore-foundation';
import { useViewport, Viewport } from '../../../../shared/lib/useViewport';
import { WizardStep } from '../lib/useWizard';
import { programPoliciesUrl, termsAndConditionsUrl } from '../lib/constants';
import { NavStepper } from './NavStepper';

type DialogFooterProps = {
  isEditing: boolean;
  prev: () => void;
  noPrev: boolean;
  hidePrev: boolean;
  next: () => void;
  noNext: boolean;
  nextCopy: string;
  step: WizardStep;
};

const TermsAgreementNotice = styled(Type).attrs({
  forwardedAs: 'p',
  variant: Type.body3,
  color: gray50,
})`
  @media (min-width: ${screenSmMin}) {
    padding-bottom: 0;
  }
  @media (max-width: ${screenSmMax}) {
    max-width: 252px;
    text-align: right;
  }
  @media (max-width: ${screenXsMax}) {
    text-align: center;
    padding-bottom: auto;
    grid-area: terms;
    justify-self: center;
  }
`;

const TermsAndNextCTAContainer = styled.div`
  display: flex;
  align-items: baseline;
  @media (min-width: ${screenSmMin}) {
    align-items: center;
  }
  @media (max-width: ${screenXsMax}) {
    flex-direction: column;
  }
`;

const FooterContainer = styled.div`
  display: flex;
  @media (max-width: ${screenXsMax}) {
    display: grid;
    grid-template-columns: 1fr minmax(0, 1fr) 1fr;
    grid-template-rows: 1fr;
    grid-template-areas: '. nav next';
  }
  @supports (-webkit-touch-callout: none) {
    /* Add padding to account for the bottom safe area on iPad */
    padding-bottom: env(safe-area-inset-bottom, auto);
  }
`;

const FooterContainerWithTerms = styled.div`
  display: flex;
  @media (max-width: ${screenXsMax}) {
    display: grid;
    grid-template-columns: 1fr minmax(0, 1fr) 1fr;
    grid-template-rows: 1fr 1fr;
    grid-template-areas:
      'terms terms terms'
      'prev nav next';
  }
  @supports (-webkit-touch-callout: none) {
    /* Add padding to account for the bottom safe area on iPad */
    padding-bottom: env(safe-area-inset-bottom, auto);
  }
`;

const LinkUnderlined = styled(TextLink)`
  text-decoration: underline;
`;

export function DialogFooter(props: DialogFooterProps) {
  const viewport = useViewport();

  const condensed = viewport === Viewport.XS;

  const confirmSelected =
    props.step === WizardStep.submit ||
    props.step === WizardStep.failure ||
    props.step === WizardStep.submitting;

  const termsAgreementNotice = confirmSelected ? (
    <TermsAgreementNotice>
      By submitting this form you're agreeing to our{' '}
      <LinkUnderlined href={termsAndConditionsUrl} target="terms">
        terms
      </LinkUnderlined>{' '}
      and{' '}
      <LinkUnderlined href={programPoliciesUrl} target="policies">
        program policies
      </LinkUnderlined>
      .
    </TermsAgreementNotice>
  ) : null;

  const nextBtn = condensed ? (
    <ButtonTertiary
      buttonSize={ButtonTertiary.sm}
      disabled={props.noNext}
      onClick={props.next}
      style={{
        color: !props.noNext ? kleinBlue : 'auto',
        gridArea: 'next',
        whiteSpace: 'nowrap',
      }}
      condensed
    >
      {props.nextCopy}
    </ButtonTertiary>
  ) : (
    <ButtonPrimary
      buttonSize={ButtonTertiary.md}
      disabled={props.noNext}
      onClick={props.next}
      style={{ marginLeft: '24px' }}
      condensed
    >
      {props.nextCopy}
    </ButtonPrimary>
  );

  const termsAndNextCTAContainer = (
    <TermsAndNextCTAContainer>
      {termsAgreementNotice}

      {nextBtn}
    </TermsAndNextCTAContainer>
  );

  const uncondensedFooterBody = (
    <>
      <ButtonTertiary
        buttonSize={condensed ? ButtonTertiary.sm : ButtonTertiary.md}
        disabled={props.noPrev}
        onClick={props.prev}
        condensed
        // Hide the button, but keep the layout the same
        style={props.hidePrev ? { visibility: 'hidden' } : {}}
      >
        Previous
      </ButtonTertiary>

      {termsAndNextCTAContainer}
    </>
  );

  const condensedFooterBody = (
    <>
      {termsAgreementNotice}

      {props.hidePrev ? null : (
        <ButtonTertiary
          buttonSize={condensed ? ButtonTertiary.sm : ButtonTertiary.md}
          disabled={props.noPrev}
          onClick={props.prev}
          condensed
          style={{ gridArea: 'prev' }}
        >
          Previous
        </ButtonTertiary>
      )}

      <NavStepper
        isEditing={props.isEditing}
        step={props.step}
        visible={condensed}
      />

      {nextBtn}
    </>
  );

  const footerBody = condensed ? condensedFooterBody : uncondensedFooterBody;

  return confirmSelected ? (
    <FooterContainerWithTerms>{footerBody}</FooterContainerWithTerms>
  ) : (
    <FooterContainer>{footerBody}</FooterContainer>
  );
}
